import { spacing } from '@constants/spacing';
import { styled, Typography } from '@mui/material';
import type { Kit } from '@partful/shared-types';
import { useProduct } from '@providers/Product';
import { getKitComponentsAndMatchingPartsFromPartTree } from '@services/kits/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Chip from 'src/components/Chip';

interface KitComponentProps {
  readonly currentKit: Kit;
}

/**
 * Displays the components of a kit and their associated details such as names, IDs, and quantities.
 *
 * @param {Object} props - The props for the KitComponents component.
 * @param {KitComponentProps} props.currentKit - The current kit object containing details to be used for rendering kit components.
 * @return {JSX.Element} A JSX element that renders the list of kit components and any associated tags or details.
 */
export function KitComponents({ currentKit }: KitComponentProps): JSX.Element {
  const { bom } = useProduct();

  const kitComponentsWithParts = useMemo(
    () =>
      // Convert the result of getKitComponentsAndMatchingPartsFromPartTree to an array of objects
      Array.from(getKitComponentsAndMatchingPartsFromPartTree(currentKit, bom), ([name, value]) => ({
        name,
        ...value,
      })),
    [currentKit, bom]
  );

  const { t } = useTranslation('partsCatalogue');

  return (
    <KitComponentsContainer>
      <KitComponentsText>{t('kitSection.kitParts')}</KitComponentsText>
      {kitComponentsWithParts.map((kitComponentWithPart) => (
        <KitComponentsList key={kitComponentWithPart.name}>
          <KitComponentsListName>
            {kitComponentWithPart.component.name} (#{kitComponentWithPart.component.externalId})
          </KitComponentsListName>
          {!kitComponentWithPart.inCAD && (
            <KitsChip label={t('kitSection.notShownChip')} color="primary" size="small" />
          )}
          {kitComponentWithPart.component.quantity > 1 && (
            <KitsChip
              label={t('kitSection.quantityChip', { quantity: kitComponentWithPart.component.quantity })}
              color="primary"
              size="small"
            />
          )}
        </KitComponentsList>
      ))}
    </KitComponentsContainer>
  );
}

const KitComponentsContainer = styled('div')`
  width: 100%;
  word-break: break-word;
  padding-top: ${spacing.smallSpacing};
`;

const KitComponentsText = styled(Typography)`
  margin-bottom: ${spacing.smallSpacing};
  font-weight: normal;
  opacity: 70%;
`;

const KitComponentsList = styled('ul')`
  padding-left: ${spacing.smallSpacing};
`;

const KitComponentsListName = styled('li')`
  padding: ${spacing.smallSpacing} 0;
  font-size: 16px;
`;

const KitsChip = styled(Chip)`
  opacity: 87%;
  color: ${({ theme }) => theme.palette.primary.contrastText};
  background-color: ${({ theme }) => theme.palette.primary.main};
  margin-right: ${spacing.smallSpacing};
`;
